import type {FilterDefinition, PropertyNamespace, PropertyNamespaceType} from '@github-ui/custom-properties-types'
import type {FilterProviderOptions} from '@github-ui/filter-core'
import {defaultFilterProviderOptions, TRUE_FALSE_FILTER_VALUES} from '@github-ui/filter-core'
import {NestedFilterProvider} from '@github-ui/filter-core/providers'
import {type Icon, NoteIcon, PlugIcon} from '@primer/octicons-react'

interface TypeConfig {
  displayName?: string
  subKeyPrefix?: string
  icon: Icon
}

type SubKeyType = 'select' | 'text'

const providerConfig: Record<PropertyNamespaceType, TypeConfig> = {
  custom: {
    displayName: 'Custom properties',
    subKeyPrefix: 'Property',
    icon: NoteIcon,
  },
  external: {
    icon: PlugIcon,
  },
}

export function getPropertyProviders(namespaces: PropertyNamespace[]) {
  return namespaces
    .filter(({definitions}) => definitions.length > 0)
    .map(ns => {
      const {namespace, definitions, type} = ns
      const {icon, subKeyPrefix, displayName} = providerConfig[type]
      const resolvedName = displayName ?? namespace

      return new NestedFilterProvider(
        {
          key: namespace,
          displayName: resolvedName,
          description: `Filter by ${displayName?.toLowerCase() ?? `${namespace} properties`}`,
          priority: 3,
          icon,
          subKeys: definitions.map(def => buildSubKey(subKeyPrefix ?? namespace, def)),
        },
        {
          filterTypes: {valueless: false, hasValue: false},
        },
      )
    })
}

function buildSubKey(prefix: string, definition: FilterDefinition) {
  const {propertyName, valueType} = definition
  const values =
    valueType === 'true_false'
      ? TRUE_FALSE_FILTER_VALUES
      : (definition.allowedValues || []).map((value, index) => ({value, displayName: value, priority: index + 1}))

  const type: SubKeyType = ['string', 'url'].includes(valueType) ? 'text' : 'select'

  return {
    key: propertyName,
    displayName: `${prefix}: ${propertyName}`,
    icon: NoteIcon,
    type,
    values,
    options: getFilterOptions(definition),
  }
}

function getFilterOptions({valueType, required}: FilterDefinition): FilterProviderOptions {
  return {
    ...defaultFilterProviderOptions,
    filterTypes: {
      multiKey: valueType === 'multi_select',
      multiValue: true,
      valueless: !required,
      hasValue: !required,
    },
  }
}
